import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  Sparkles, 
  Check, 
  Phone, 
  Mail, 
  Calendar, 
  ShieldCheck, 
  Clock, 
  MapPin, 
  Heart, 
  AlertCircle, 
  Compass, 
  HelpCircle,
  Eye,
  BookOpen
} from "lucide-react";
import ContactSection from "../components/ContactSection";
import FaqSection from "../components/FaqSection";

export default function Hypnose() {
  return (
    <>
      <Helmet>
        <title>Hypnose i Viby J ved Aarhus | Tryg støtte til forandring | John Calmar</title>
        <meta
          name="description"
          content="Hypnose og hypnoterapi i rolige rammer i Viby J ved Aarhus hos John Calmar. Få tryg støtte til forandring af vaner, fobier, frygt og angst."
        />
        <link rel="canonical" href="https://johncalmar.dk/hypnose" />
        <meta property="og:title" content="Hypnose i Viby J ved Aarhus | Tryg støtte til forandring | John Calmar" />
        <meta
          property="og:description"
          content="Hypnose og hypnoterapi i rolige rammer i Viby J ved Aarhus hos John Calmar. Få tryg støtte til forandring af vaner, fobier, frygt og angst."
        />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/hypnose-hypnoterapi-behandling.png"
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Hypnose i Viby J ved Aarhus | Tryg støtte til forandring | John Calmar" />
        <meta
          name="twitter:description"
          content="Hypnose og hypnoterapi i rolige rammer i Viby J ved Aarhus hos John Calmar. Få tryg støtte til forandring af vaner, fobier, frygt og angst."
        />
        <meta
          name="twitter:image"
          content="https://assets.auxodev.dk/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/hypnose-hypnoterapi-behandling.png"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-brand-50 py-20 md:py-28 overflow-hidden border-b border-brand-200/40">
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-brand-500/10 rounded-full blur-3xl pointer-events-none" />
        
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Hero Content */}
          <div className="lg:col-span-7 text-left space-y-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-100/60 border border-brand-300/60 text-brand-800 text-xs font-semibold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5 text-brand-600" />
              <span>Viby J ved Aarhus • Jacob Strachotta uddannet</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] font-light text-neutral-950 leading-[1.2] tracking-tight">
              Hypnose i Aarhus & Viby J{" "}
              <span className="font-display italic text-brand-600 inline font-normal">
                – tryg støtte til forandring
              </span>
            </h1>

            <div className="space-y-3.5 text-neutral-700 text-base sm:text-lg font-light leading-relaxed max-w-2xl">
              <p className="text-lg sm:text-xl text-neutral-800 font-normal">
                Søger du professionel hypnose i Aarhus eller omegn? Ønsker du at ændre en vane, en frygt eller et mønster, som fylder for meget, tilbyder jeg nærværende hypnose i rolige rammer i Viby J ved Aarhus hos{" "}
                <Link
                  to="/"
                  className="text-neutral-900 font-medium hover:text-brand-600 underline underline-offset-4 decoration-brand-400/60 hover:decoration-brand-600 transition-colors"
                >
                  John Calmar
                </Link>
                .
              </p>
              <p>
                Med hypnose i Aarhus tager vi altid udgangspunkt i dit konkrete mål, dine personlige grænser og det tempo, der føles trygt for dig, så du opnår en fokuseret og overskuelig proces.
              </p>
              <p className="text-neutral-600 text-sm sm:text-base pt-1 border-l-2 border-brand-200 pl-3.5">
                Sideløbende med hypnose arbejder jeg også med{" "}
                <Link
                  to="/clairvoyance"
                  className="text-neutral-900 font-medium hover:text-brand-600 underline underline-offset-4 decoration-brand-400/60 hover:decoration-brand-600 transition-colors"
                >
                  clairvoyance
                </Link>
                , hvis du har brug for et nyt overblik og klarhed over din situation.
              </p>
            </div>

            {/* USPs directly in the Hero Area */}
            <div className="space-y-3 max-w-xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 text-sm text-neutral-700">
                  <Check className="w-4 h-4 text-brand-600 shrink-0" />
                  <span>Uddannet hypnotisør & hypnoterapeut</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-neutral-700">
                  <Check className="w-4 h-4 text-brand-600 shrink-0" />
                  <span>Mere end 40 års erfaring</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-neutral-700">
                  <Check className="w-4 h-4 text-brand-600 shrink-0" />
                  <span>Du bevarer fuld kontrol</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-neutral-700">
                  <Check className="w-4 h-4 text-brand-600 shrink-0" />
                  <span>Rolige rammer i Viby J</span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="pt-2 flex flex-col sm:flex-row gap-4">
              <a
                href="#kontakt"
                className="px-8 py-4 bg-brand-600 hover:bg-brand-700 text-white hover:text-white font-semibold rounded-xl text-center flex items-center justify-center gap-2 transition-all duration-200 shadow-lg shadow-brand-600/10"
              >
                <Calendar className="w-4 h-4" />
                Bestil en session nu
              </a>
              <a
                href="tel:40938593"
                className="px-8 py-4 bg-white hover:bg-neutral-50 border border-neutral-200 hover:border-neutral-300 text-neutral-800 font-semibold rounded-xl text-center flex items-center justify-center gap-2 transition-all duration-200"
              >
                <Phone className="w-4 h-4" />
                +45 40 93 85 93
              </a>
            </div>
          </div>

          {/* Hero Image */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <div className="relative w-full aspect-[4/5] overflow-hidden border border-brand-200/60 shadow-2xl rounded-2xl">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/a3d6ce76-96f3-4371-8c41-b2ca08e435bb/john-calmar-hypnoterapi-aarhus-klient.jpg"
                alt="John Calmar med klient – Certificeret hypnotisør og hypnoterapeut i Viby J ved Aarhus"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm p-4 rounded-xl border border-neutral-200 text-left shadow-md">
                <p className="font-display font-medium text-neutral-900 text-sm">John Calmar</p>
                <p className="text-xs text-neutral-600">Uddannet hypnotisør & hypnoterapeut hos Jacob Strachotta</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main SEO Content Section with Sticky Sidebar */}
      <section className="bg-white py-16 md:py-24 border-y border-neutral-100">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* Primary Article Content (7 cols on desktop) */}
            <div className="lg:col-span-7 space-y-14 text-left">

              {/* Section 1: Hvad er hypnose, og hvordan opleves tilstanden? */}
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-50 border border-brand-200/60 text-brand-700 text-xs font-semibold uppercase tracking-wider">
                  <Sparkles className="w-3.5 h-3.5 text-brand-600" />
                  <span>Ro & fokuseret opmærksomhed</span>
                </div>
                
                <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-light text-neutral-950 leading-tight">
                  Hvad er hypnose, og hvordan opleves tilstanden?
                </h2>

                {/* Lead highlight card */}
                <div className="relative bg-gradient-to-br from-brand-50/70 via-white to-brand-50/40 border-l-4 border-brand-600 border-y border-r border-neutral-200/70 rounded-2xl p-6 sm:p-7 space-y-4 shadow-sm">
                  <p className="text-neutral-800 text-lg sm:text-xl font-display font-light leading-relaxed">
                    Hvad er hypnose egentlig? Det er en tilstand med ro og fokuseret opmærksomhed, hvor du kan blive mere optaget af indre billeder, ord og forestillinger. Oplevelsen kan minde om at falde i staver eller være helt opslugt af en bog. Du sover ikke, og du kan stadig høre og reagere.
                  </p>

                  {/* Oplevelsens kendetegn */}
                  <div className="pt-2 border-t border-brand-200/50">
                    <span className="text-xs uppercase tracking-wider text-neutral-500 font-semibold block mb-2.5">
                      Tilstanden kendetegnes ved:
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div className="bg-white/90 border border-brand-200/70 rounded-xl px-3 py-2 text-center shadow-xs">
                        <span className="text-xs font-medium text-neutral-800 flex items-center justify-center gap-1.5">
                          <Sparkles className="w-3 h-3 text-brand-600" />
                          Dyb indre ro
                        </span>
                      </div>
                      <div className="bg-white/90 border border-brand-200/70 rounded-xl px-3 py-2 text-center shadow-xs">
                        <span className="text-xs font-medium text-neutral-800 flex items-center justify-center gap-1.5">
                          <BookOpen className="w-3 h-3 text-brand-600" />
                          Som at fordybe sig i en bog
                        </span>
                      </div>
                      <div className="bg-white/90 border border-brand-200/70 rounded-xl px-3 py-2 text-center shadow-xs">
                        <span className="text-xs font-medium text-neutral-800 flex items-center justify-center gap-1.5">
                          <Eye className="w-3 h-3 text-brand-600" />
                          Du sover ikke
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 text-neutral-600 font-light text-base sm:text-lg leading-relaxed">
                  <p>
                    Hypnose er selve tilstanden, mens hypnoterapi er det målrettede arbejde, vi udfører i denne tilstand. Jeg bruger guidning og samtale til at støtte arbejdet med vaner, tanker og følelsesmæssige reaktioner. Metoden er ikke magi, og jeg kan ikke love et bestemt resultat.
                  </p>
                </div>

                {/* Definition bar */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <div className="bg-neutral-50 border border-neutral-200/70 rounded-xl p-4 text-center">
                    <span className="text-xs font-semibold text-brand-700 uppercase tracking-wider block">Hypnose</span>
                    <span className="text-xs text-neutral-600 font-light leading-tight block mt-1">Selve tilstanden med ro og fokuseret opmærksomhed</span>
                  </div>
                  <div className="bg-neutral-50 border border-neutral-200/70 rounded-xl p-4 text-center">
                    <span className="text-xs font-semibold text-brand-700 uppercase tracking-wider block">Hypnoterapi</span>
                    <span className="text-xs text-neutral-600 font-light leading-tight block mt-1">Det målrettede arbejde med vaner, tanker og følelser</span>
                  </div>
                </div>
              </div>

              {/* Section 2: Har du stadig kontrollen under hypnose? */}
              <div className="space-y-5 pt-8 border-t border-neutral-100">
                <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-light text-neutral-950 leading-tight">
                  Har du stadig kontrollen under hypnose?
                </h2>
                
                <div className="bg-stone-50/80 border border-stone-200/80 rounded-2xl p-6 sm:p-7 space-y-4">
                  <div className="flex items-start gap-3.5">
                    <div className="w-9 h-9 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center shrink-0 mt-0.5">
                      <ShieldCheck className="w-5 h-5" />
                    </div>
                    <div className="space-y-3">
                      <p className="text-neutral-800 text-base sm:text-lg font-light leading-relaxed">
                        Du bevarer din egen vilje under hele sessionen. Du kan sige fra, stille spørgsmål eller åbne øjnene, når du ønsker det. Jeg kan ikke få dig til at gøre noget, du ikke vil. Min rolle er at guide dig roligt, mens du selv deltager aktivt i den forandring, du ønsker at undersøge.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-2 border-t border-stone-200/60">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white text-xs font-medium text-neutral-700 border border-neutral-200 shadow-xs">
                      <Check className="w-3.5 h-3.5 text-brand-600" />
                      Du bevarer din egen frie vilje
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white text-xs font-medium text-neutral-700 border border-neutral-200 shadow-xs">
                      <Check className="w-3.5 h-3.5 text-brand-600" />
                      Du kan altid sige fra eller åbne øjnene
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white text-xs font-medium text-neutral-700 border border-neutral-200 shadow-xs">
                      <Check className="w-3.5 h-3.5 text-brand-600" />
                      Rolig guidning i dit tempo
                    </span>
                  </div>
                </div>
              </div>

              {/* Section 3: Hvordan kan hypnose mod fobier og angst støtte dig? */}
              <div className="space-y-6 pt-8 border-t border-neutral-100">
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 text-brand-700 text-xs font-semibold uppercase tracking-wider">
                    <Heart className="w-3.5 h-3.5 text-brand-600" />
                    <span>Nænsomt arbejde med krop & sind</span>
                  </div>
                  <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-light text-neutral-950 leading-tight">
                    Hvordan kan hypnose mod fobier og angst støtte dig?
                  </h2>
                </div>

                <p className="text-neutral-600 font-light text-base sm:text-lg leading-relaxed">
                  Hypnose mod angst kan være en støtte, når uro, bekymringer eller bestemte situationer sætter automatiske reaktioner i gang. Ved hypnose mod fobier arbejder vi nænsomt med din oplevelse af frygt og med den ro og handlefrihed, du gerne vil have mere af. Du bliver ikke presset.
                </p>

                {/* Hvad ønsker du at kunne gøre anderledes? */}
                <div className="space-y-4 pt-2">
                  <h3 className="font-display text-xl sm:text-2xl font-light text-neutral-900 leading-tight">
                    Hvad ønsker du at kunne gøre anderledes?
                  </h3>
                  <p className="text-neutral-600 font-light text-base sm:text-lg leading-relaxed">
                    Når vi arbejder med frygt, vælger vi et tydeligt mål, som giver mening i din hverdag. Det handler ikke om at presse dig ind i en situation, du ikke er klar til. Vi retter i stedet opmærksomheden mod den reaktion, du ønsker, og tager hensyn til dine grænser undervejs.
                  </p>
                </div>

                {/* 3 cards for specific focus areas */}
                <div className="grid grid-cols-1 gap-4 pt-2">
                  <div className="group bg-gradient-to-br from-white to-brand-50/30 border border-neutral-200/90 hover:border-brand-300 rounded-2xl p-5 sm:p-6 transition-all shadow-xs space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-xl bg-brand-100/80 text-brand-700 flex items-center justify-center shrink-0">
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <h4 className="font-display font-medium text-neutral-900 text-lg">
                        Angst og uro
                      </h4>
                    </div>
                    <p className="text-sm text-neutral-700 font-light leading-relaxed pl-11">
                      Angst og uro hvor fokus kan være at møde kropslige signaler og bekymringer på en mere rolig og bevidst måde.
                    </p>
                  </div>

                  <div className="group bg-gradient-to-br from-white to-brand-50/30 border border-neutral-200/90 hover:border-brand-300 rounded-2xl p-5 sm:p-6 transition-all shadow-xs space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-xl bg-brand-100/80 text-brand-700 flex items-center justify-center shrink-0">
                        <Check className="w-4 h-4" />
                      </div>
                      <h4 className="font-display font-medium text-neutral-900 text-lg">
                        Fobier og konkret frygt
                      </h4>
                    </div>
                    <p className="text-sm text-neutral-700 font-light leading-relaxed pl-11">
                      Fobier og konkret frygt som flyskræk, højdeskræk eller frygt for dyr, hvor vi aftaler et trygt og overskueligt tempo.
                    </p>
                  </div>

                  <div className="group bg-gradient-to-br from-white to-brand-50/30 border border-neutral-200/90 hover:border-brand-300 rounded-2xl p-5 sm:p-6 transition-all shadow-xs space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-xl bg-brand-100/80 text-brand-700 flex items-center justify-center shrink-0">
                        <Check className="w-4 h-4" />
                      </div>
                      <h4 className="font-display font-medium text-neutral-900 text-lg">
                        Vaner og fastlåste mønstre
                      </h4>
                    </div>
                    <p className="text-sm text-neutral-700 font-light leading-relaxed pl-11">
                      Vaner og fastlåste mønstre som sukkertrang eller overspisning, hvor vi retter opmærksomheden mod nye valg og reaktioner.
                    </p>
                  </div>

                  <div className="group bg-gradient-to-br from-white to-brand-50/30 border border-neutral-200/90 hover:border-brand-300 rounded-2xl p-5 sm:p-6 transition-all shadow-xs space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-xl bg-brand-100/80 text-brand-700 flex items-center justify-center shrink-0">
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <h4 className="font-display font-medium text-neutral-900 text-lg">
                        Rygestop
                      </h4>
                    </div>
                    <p className="text-sm text-neutral-700 font-light leading-relaxed pl-11">
                      Rygestop med hypnose hvor vi arbejder målrettet med at bryde den mentale afhængighed og automatiske vaner, så du finder varig ro og motivation som røgfri. Du kan også bestille en rygestop-pakke direkte i min <Link to="/butik" className="text-brand-700 hover:text-brand-800 underline font-normal">online butik</Link>.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 4: Hypnose og depression, hvornår kræver det ekstra omtanke? */}
              <div className="p-6 sm:p-8 rounded-2xl bg-amber-50/50 border border-amber-200/80 space-y-4 shadow-xs">
                <div className="flex items-center gap-2 text-amber-800 text-xs font-semibold uppercase tracking-wider">
                  <AlertCircle className="w-4 h-4 text-amber-700" />
                  <span>Omtanke & sundhedsfagligt ansvar</span>
                </div>
                <h2 className="font-display text-2xl sm:text-3xl font-light text-neutral-950 leading-tight">
                  Hypnose og depression, hvornår kræver det ekstra omtanke?
                </h2>
                <div className="space-y-3 text-neutral-700 font-light text-base leading-relaxed">
                  <p>
                    En søgning på hypnose depression kan give indtryk af, at der findes en enkel løsning. Depression er ofte kompleks, og jeg betragter ikke hypnose som en kur. Hypnoterapi kan i nogle tilfælde være et supplement til arbejdet med selvomsorg, fastlåste tanker og små ønskede skridt.
                  </p>
                  <p className="p-4 rounded-xl bg-white/80 border border-amber-200 text-amber-950 text-sm font-medium leading-relaxed">
                    Har du tegn på depression eller alvorlig angst, bør du kontakte din læge eller en autoriseret sundhedsfaglig behandler. Er du allerede i behandling, skal hypnose ikke erstatte den. Ved akut mistrivsel eller tanker om at skade dig selv skal du søge akut hjælp med det samme.
                  </p>
                </div>
              </div>

              {/* Section 5: Sådan foregår hypnose hos John Calmar */}
              <div className="space-y-6 pt-8 border-t border-neutral-100">
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 text-brand-700 text-xs font-semibold uppercase tracking-wider">
                    <Clock className="w-3.5 h-3.5 text-brand-600" />
                    <span>Forløb & tryghed</span>
                  </div>
                  <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-light text-neutral-950 leading-tight">
                    Sådan foregår hypnose hos John Calmar
                  </h2>
                </div>

                <p className="text-neutral-600 font-light text-base sm:text-lg leading-relaxed">
                  Vi begynder med en samtale om det, du ønsker at arbejde med, og om hvad der skal til, for at du føler dig tryg. Herefter guider jeg dig ind i en rolig og fokuseret tilstand ved hjælp af ord, opmærksomhed og forestillinger. Til sidst samler vi op på din oplevelse.
                </p>

                {/* 3 Step Visual Flow */}
                <div className="space-y-4">
                  {/* Step 1 */}
                  <div className="flex items-start gap-4 p-5 sm:p-6 bg-white border border-neutral-200/80 rounded-2xl shadow-xs">
                    <div className="w-8 h-8 rounded-full bg-brand-600 text-white font-bold text-sm flex items-center justify-center shrink-0 mt-0.5">
                      1
                    </div>
                    <div className="space-y-1.5">
                      <h3 className="font-display font-medium text-neutral-900 text-lg">
                        Før hypnosen
                      </h3>
                      <p className="text-neutral-600 font-light text-base leading-relaxed">
                        Før hypnosen afklarer vi dit mål, dine spørgsmål og de grænser, jeg skal kende.
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex items-start gap-4 p-5 sm:p-6 bg-white border border-neutral-200/80 rounded-2xl shadow-xs">
                    <div className="w-8 h-8 rounded-full bg-brand-600 text-white font-bold text-sm flex items-center justify-center shrink-0 mt-0.5">
                      2
                    </div>
                    <div className="space-y-1.5">
                      <h3 className="font-display font-medium text-neutral-900 text-lg">
                        Under hypnosen
                      </h3>
                      <p className="text-neutral-600 font-light text-base leading-relaxed">
                        Under hypnosen følger du min guidning og er hele tiden i stand til at reagere.
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex items-start gap-4 p-5 sm:p-6 bg-white border border-neutral-200/80 rounded-2xl shadow-xs">
                    <div className="w-8 h-8 rounded-full bg-brand-600 text-white font-bold text-sm flex items-center justify-center shrink-0 mt-0.5">
                      3
                    </div>
                    <div className="space-y-1.5">
                      <h3 className="font-display font-medium text-neutral-900 text-lg">
                        Efter hypnosen
                      </h3>
                      <p className="text-neutral-600 font-light text-base leading-relaxed">
                        Efter hypnosen taler vi om oplevelsen og om det fokus, du tager med videre.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Om John Calmar baggrund */}
                <div className="p-6 sm:p-7 rounded-2xl bg-neutral-50 border border-neutral-200/80 space-y-3">
                  <p className="text-neutral-700 font-light text-base sm:text-lg leading-relaxed">
                    Jeg har mere end 40 års erfaring med at møde mennesker i min praksis og er uddannet hypnotisør og hypnoterapeut hos Jacob Strachotta. Min tilgang er personlig, omsorgsfuld og uden fordømmelse. Ingen mennesker er ens, og derfor tager hver session udgangspunkt i din situation.
                  </p>
                </div>
              </div>

              {/* Section 6: Er hypnose det rigtige næste skridt for dig? */}
              <div className="bg-gradient-to-br from-brand-50/80 via-white to-brand-100/50 border border-brand-300/70 rounded-2xl p-6 sm:p-8 space-y-6 shadow-sm">
                <div className="space-y-3">
                  <div className="inline-flex items-center gap-2 text-brand-700 text-xs font-semibold uppercase tracking-wider">
                    <HelpCircle className="w-4 h-4 text-brand-600" />
                    <span>Næste skridt</span>
                  </div>
                  <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-light text-neutral-950 leading-tight">
                    Er hypnose i Aarhus det rigtige næste skridt for dig?
                  </h2>
                  <p className="text-neutral-700 text-base sm:text-lg font-light leading-relaxed">
                    Du behøver ikke kende hele løsningen, før du kontakter mig. Det er nok, at du kan sætte ord på det, der fylder, og den forandring, du håber på. Vælger du hypnose i Aarhus hos mig, foregår sessionerne i rolige omgivelser i Viby J tæt på Aarhus, og du er altid velkommen til uforpligtende at spørge ind til metoden og rammerne, før du bestiller en tid.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <a
                    href="#kontakt"
                    className="px-8 py-4 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-xl text-center flex items-center justify-center gap-2 transition-all shadow-md"
                  >
                    <Calendar className="w-4 h-4" />
                    Bestil tid til hypnose
                  </a>
                  <a
                    href="tel:40938593"
                    className="px-8 py-4 bg-white hover:bg-neutral-50 border border-neutral-300 text-neutral-800 font-semibold rounded-xl text-center flex items-center justify-center gap-2 transition-all"
                  >
                    <Phone className="w-4 h-4" />
                    Ring og spørg: 40 93 85 93
                  </a>
                </div>
              </div>

            </div>

            {/* Sticky Sidebar: Pricing Box & Practical Info (5 cols on desktop) */}
            <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-28">
              
              {/* Prisboks */}
              <div className="border border-neutral-950 rounded-2xl p-6 sm:p-7 bg-brand-50/40 shadow-sm space-y-6">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-brand-700 block mb-1">
                    Overskuelige priser
                  </span>
                  <h3 className="text-2xl font-display font-medium text-neutral-950">
                    Hypnose i Aarhus & Viby J
                  </h3>
                  <p className="text-xs text-neutral-600 mt-1 font-light">
                    Klinik og praksis i rolige rammer i Viby J ved Aarhus
                  </p>
                </div>

                <ul className="space-y-4 text-neutral-800">
                  <li className="flex justify-between items-center pb-3 border-b border-neutral-200/80">
                    <div>
                      <span className="font-medium text-neutral-900 block text-base">Enkelt session</span>
                      <span className="text-xs text-neutral-500 font-light">1 ½ time</span>
                    </div>
                    <span className="font-bold text-brand-700 text-xl">1.200 kr.</span>
                  </li>
                  <li className="flex justify-between items-center pb-3 border-b border-neutral-200/80">
                    <div>
                      <span className="font-medium text-neutral-900 block text-base">Forløb (3 sessioner)</span>
                      <span className="text-xs text-neutral-500 font-light">3 gange fordybelse</span>
                    </div>
                    <span className="font-bold text-brand-700 text-xl">3.200 kr.</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <div>
                      <span className="font-medium text-neutral-900 block text-base">Rygestop</span>
                      <span className="text-xs text-neutral-500 font-light">Målrettet pakke</span>
                    </div>
                    <span className="font-bold text-brand-700 text-xl">1.600 kr.</span>
                  </li>
                </ul>

                <p className="text-xs text-neutral-500 mt-4 pt-3 border-t border-neutral-200/60 leading-relaxed">
                  Sessionerne kan også købes og forudbetales direkte via min <Link to="/butik" className="text-brand-700 hover:text-brand-800 underline font-medium">online butik</Link>.
                </p>

                <div className="pt-2">
                  <a
                    href="#kontakt"
                    className="w-full py-3.5 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-xl text-center flex items-center justify-center gap-2 transition-all text-sm shadow-sm"
                  >
                    <Calendar className="w-4 h-4" />
                    Bestil tid nu
                  </a>
                </div>
              </div>

              {/* Praksis Lokation Boks */}
              <div className="border border-neutral-200/80 rounded-2xl p-6 bg-white shadow-xs space-y-4">
                <div className="flex items-center gap-2 text-brand-700 text-xs font-semibold uppercase tracking-wider">
                  <MapPin className="w-4 h-4 text-brand-600" />
                  <span>Klinik & praksis</span>
                </div>
                <div>
                  <h4 className="font-display font-medium text-neutral-900 text-lg">
                    Rolige rammer i Viby J
                  </h4>
                  <p className="text-sm text-neutral-600 font-light mt-1 leading-relaxed">
                    Lykkesholms Allé 102, 1. th.<br />
                    8260 Viby J (Aarhus)
                  </p>
                </div>
                <div className="pt-2 border-t border-neutral-100 flex flex-col gap-2 text-xs text-neutral-600">
                  <span className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-brand-600" />
                    Gratis parkering tæt ved døren
                  </span>
                  <span className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-brand-600" />
                    Let adgang med offentlig transport
                  </span>
                </div>
              </div>

              {/* Tryghed & kontrol boks */}
              <div className="border border-brand-200/70 rounded-2xl p-6 bg-brand-50/50 space-y-3">
                <div className="flex items-center gap-2 text-neutral-800 text-xs font-semibold uppercase tracking-wider">
                  <ShieldCheck className="w-4 h-4 text-brand-600" />
                  <span>Altid i trygge hænder</span>
                </div>
                <p className="text-xs text-neutral-600 leading-relaxed font-light">
                  Uddannet hos Jacob Strachotta. Læs mere <Link to="/om-john" className="text-brand-700 hover:text-brand-800 underline">om min historie og baggrund</Link>, eller se hvad andre klienter fortæller under <Link to="/referencer" className="text-brand-700 hover:text-brand-800 underline">klient-referencer</Link>.
                </p>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection
        title="Ofte stillede spørgsmål om hypnose"
        description="Her kan du finde svar på typiske spørgsmål om hypnosetilstanden, tryghed, forløb og hvad hypnoterapi kan hjælpe med."
        badge="Spørgsmål & Svar"
        items={[
          {
            question: "Mister jeg kontrollen, eller kan jeg tvinges til at gøre noget mod min vilje?",
            answer:
              "Nej, du mister på intet tidspunkt kontrollen over dig selv under en hypnosesession. Du bevarer din fulde bevidsthed, dine personlige værdier og din frie vilje fra start til slut. Du kan til enhver tid tale, svare, sige fra eller blot vælge at åbne øjnene. Min opgave er udelukkende at guide dig ind i en rolig tilstand, hvor du selv og i eget tempo arbejder med den ønskede forandring."
          },
          {
            question: "Hvordan føles det egentlig at være i hypnose – sover eller besvimer man?",
            answer:
              "Hypnose er en helt naturlig tilstand præget af dyb kropslig afslapning og et skærpet, indre fokus. Du sover ikke, og du mister aldrig bevidstheden undervejs. Oplevelsen minder meget om at falde i staver, meditere eller være fuldkommen opslugt af en medrivende bog eller film. Du hører alt hvad der bliver sagt i rummet, men oplever blot en behagelig ro og mentalt overskud."
          },
          {
            question: "Hvor mange sessioner skal der typisk til for at opnå en mærkbar effekt?",
            answer:
              "Det nødvendige antal sessioner varierer efter dit emne, vanens karakter og din personlige modtagelighed. Ved et målrettet rygestop er en enkelt grundig session ofte nok, mens dybereliggende mønstre, fobier eller længerevarende angst typisk kræver et forløb på 2 til 3 sessioner. Langt de fleste mærker allerede en markant positiv lettelse og en forandret reaktion efter den allerførste gang."
          },
          {
            question: "Kan alle mennesker hypnotiseres, eller kræver det en særlig modtagelighed?",
            answer:
              "Stort set alle mentalt sunde mennesker kan hypnotiseres, så længe de selv er motiverede og ønsker det. Hypnose er en medfødt mental evne, som vi alle oplever dagligt i overgangsfasen mellem vågen tilstand og søvn. Det forudsætter ingen særlige talenter eller forudgående erfaring – det eneste afgørende er dit eget ønske om positiv forandring samt tillid til den rolige guidning."
          },
          {
            question: "Hvilke typer af udfordringer kan hypnose og hypnoterapi hjælpe mig med?",
            answer:
              "Hypnoterapi er særdeles virkningsfuldt til at omkode uhensigtsmæssige vaner og fastlåste tankemønstre i underbevidstheden. Metoden har stor succes ved rygestop, vægttab og sukkertrang, eksamensskræk, tandlægeskræk, flyskræk samt ved bearbejdning af angst og hverdagsstress. Gennem nænsomt arbejde med kroppens og sindets reaktioner hjælpes du tilbage til balance, overskud og indre styrke."
          }
        ]}
      />

      {/* Contact Section */}
      <ContactSection 
        title="Bestil tid til Hypnose" 
        description="Udfyld formularen her med dine oplysninger og beskriv kort, hvad du ønsker hjælp til (f.eks. rygestop, vægttab, angst eller vaner). Jeg kontakter dig hurtigst muligt for at aftale din første session." 
      />
    </>
  );
}
